import { NumericFormat } from 'react-number-format';
import { formatPercentage } from './utils/formatUtils';
import { useColumnWidths } from './useColumnWidths';

const CashFlowSummary = ({
  columns,
  cashFlowData,
  yearlyTotals,
  settings = { currencySymbol: '$', groupSeparator: ',', decimalSeparator: '.' }
}) => {
  const { columnWidths, getWidth } = useColumnWidths();
  const w = (colName) => ({ width: columnWidths[colName] ?? getWidth(colName), minWidth: columnWidths[colName] ?? getWidth(colName), flexShrink: 0 });

  const getCol = (name) => columns.find(c => c.name === name);
  const descriptionCol         = getCol("Description");
  const forecastAllocationCol  = getCol("For. Allocation");
  const forecastHoursCol       = getCol("For. Hours");
  const actualHoursCol         = getCol("Act. Hours");
  const ForecastHvCol       = getCol("For. HV");
  const forecastHCostCol       = getCol("For. h. Cost");
  const forecastHBillCol       = getCol("For. h. Bill");
  const forecastTotalCostCol   = getCol("For. Total Cost");
  const forecastTotalBillCol   = getCol("For. Total Bill");
  const forecastCvCol          = getCol("For. CV");
  const forecastGpCol           = getCol("For. GP");
  const invCol                 = getCol("INV");
  const acCol                  = getCol("AC");
  const gpCol                  = getCol("GP");
  const totalsCol              = getCol("Totals");

  const getMonthForecastTotals = (cf) => {
    const allLines = cf.lines || [];
    const parentIds = new Set(allLines.filter(l => l.parentLineId).map(l => l.parentLineId));
    const lines = allLines.filter(l => !parentIds.has(l.id));
    const totalForecastHours     = lines.reduce((s, l) => s + (l.forecastHours     || 0), 0);
    const totalForecastTotalCost = lines.reduce((s, l) => s + (l.forecastTotalCost || 0), 0);
    const totalForecastTotalBill = lines.reduce((s, l) => s + (l.forecastTotalBill || 0), 0);
    return {
      avgForecastAllocation:     lines.reduce((s, l) => s + (l.forecastAllocation  || 0), 0),
      totalForecastHours,
      avgForecastHourlyCostRate: totalForecastHours > 0 ? totalForecastTotalCost / totalForecastHours : 0,
      avgForecastHourlyBillRate: totalForecastHours > 0 ? totalForecastTotalBill / totalForecastHours : 0,
      totalForecastTotalCost,
      totalForecastTotalBill,
      totalCv:                   lines.reduce((s, l) => s + (l.cv                  || 0), 0),
      totalActualHours:          lines.reduce((s, l) => s + (l.actualHours         || 0), 0),
      totalForecastHv:           lines.reduce((s, l) => s + (l.ForecastHv          || 0), 0),
    };
  };

  const renderSummaryCells = (ft, totalInvVal, totalAcVal) => {
    const gpPct = totalInvVal > 0 ? ((totalInvVal - totalAcVal) / totalInvVal) * 100 : 0;
    const forecastGpPct = ft.totalForecastTotalBill > 0 ? ((ft.totalForecastTotalBill - ft.totalForecastTotalCost) / ft.totalForecastTotalBill) * 100 : 0;
    return (
      <div className="in-out-inputs">
        {forecastAllocationCol?.isVisible  && <NumericFormat style={w("For. Allocation")}  value={ft.avgForecastAllocation}    decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastHoursCol?.isVisible       && <NumericFormat style={w("For. Hours")}       value={ft.totalForecastHours}       decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {actualHoursCol?.isVisible         && <NumericFormat style={w("Act. Hours")}       value={ft.totalActualHours}        decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {ForecastHvCol?.isVisible       && <NumericFormat style={w("For. HV")}       value={ft.totalForecastHv}      decimalScale={2} displayType="text" thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className={`forecast-cell cv-cell ${(ft.totalForecastHv < 0) && 'cv-slipping'}`} />}
        {forecastHCostCol?.isVisible       && <NumericFormat style={w("For. h. Cost")}     value={ft.avgForecastHourlyCostRate} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastHBillCol?.isVisible       && <NumericFormat style={w("For. h. Bill")}     value={ft.avgForecastHourlyBillRate} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastTotalCostCol?.isVisible   && <NumericFormat style={w("For. Total Cost")}  value={ft.totalForecastTotalCost}   decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {forecastTotalBillCol?.isVisible   && <NumericFormat style={w("For. Total Bill")}  value={ft.totalForecastTotalBill}   decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="forecast-cell" />}
        {invCol?.isVisible  && <NumericFormat style={w("INV")} value={totalInvVal} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="in-amount" />}
        {acCol?.isVisible   && <NumericFormat style={w("AC")}  value={totalAcVal}  decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className="out-amount" />}
        {forecastCvCol?.isVisible && <NumericFormat style={w("For. CV")} value={ft.totalCv} decimalScale={2} displayType="text" prefix={settings.currencySymbol} thousandSeparator={settings.groupSeparator} decimalSeparator={settings.decimalSeparator} className={`forecast-cell cv-cell ${(ft.totalCv < 0) && 'cv-slipping'}`} />}
        {forecastGpCol?.isVisible && <input style={w("For. GP")} type="text" className="gp-percentage" disabled value={formatPercentage(forecastGpPct)} />}
        {gpCol?.isVisible   && <input style={w("GP")} type="text" className="gp-percentage" disabled value={formatPercentage(gpPct)} />}
      </div>
    );
  };

  const yearlyFt = {
    avgForecastAllocation:     yearlyTotals.avgForecastAllocation     || 0,
    totalForecastHours:        yearlyTotals.totalForecastHours        || 0,
    avgForecastHourlyCostRate: yearlyTotals.avgForecastHourlyCostRate || 0,
    avgForecastHourlyBillRate: yearlyTotals.avgForecastHourlyBillRate || 0,
    totalForecastTotalCost:    yearlyTotals.totalForecastTotalCost    || 0,
    totalForecastTotalBill:    yearlyTotals.totalForecastTotalBill    || 0,
    totalCv:                   yearlyTotals.totalCv                   || 0,
    totalActualHours:          yearlyTotals.totalActualHours          || 0,
    totalForecastHv:        yearlyTotals.totalForecastHv        || 0,
  };

  return (
    <tr className="summary-row">
      <td className="category-column summary-label">Total</td>
      {descriptionCol?.isVisible !== false && <td className="description-column summary-label"></td>}
      {cashFlowData.map(cf => {
        const ft = getMonthForecastTotals(cf);
        return (
          <td key={`summary-${cf.year}-${cf.month}`} className="summary-value">
            <div className="month-column-content">
              {renderSummaryCells(ft, cf.totalInv || 0, cf.totalAc || 0)}
            </div>
          </td>
        );
      })}
      {totalsCol?.isVisible !== false && (
        <td className="totals-column summary-total">
          <div className="month-column-content">
            {renderSummaryCells(yearlyFt, yearlyTotals.totalInv || 0, yearlyTotals.totalAc || 0)}
          </div>
        </td>
      )}
    </tr>
  );
};

export default CashFlowSummary;